import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
} from '@/components/ui/alert-dialog';
import { Plus, Trash2, Edit, ExternalLink, GripVertical, AlertTriangle } from 'lucide-react';
import { CUSTOM_TOOL_ICONS, CUSTOM_TOOL_COLORS, getCustomToolIcon } from '@/config/tools';

// Available icon names for the select dropdown
const AVAILABLE_ICONS = Object.keys(CUSTOM_TOOL_ICONS);

/**
 * CustomToolsManager - UI component for managing custom iframe-based tools in a space
 *
 * @param {Array} customTools - Array of custom tool objects
 * @param {Function} onChange - Callback when tools are modified (receives updated array)
 */
export function CustomToolsManager({ customTools = [], onChange }) {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [deletingTool, setDeletingTool] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    icon: 'ExternalLink',
    color: '#6366f1'
  });
  const [urlError, setUrlError] = useState('');
  const [urlWarning, setUrlWarning] = useState('');

  // Validate URL - returns { error, warning }
  const validateUrl = useCallback((url) => {
    if (!url.trim()) {
      return { error: 'URL is required', warning: '' };
    }
    try {
      const parsed = new URL(url);
      if (!['http:', 'https:'].includes(parsed.protocol)) {
        return { error: 'URL must start with http:// or https://', warning: '' };
      }
      // Allow HTTP but warn about security
      if (parsed.protocol === 'http:' && !parsed.hostname.includes('localhost') && !parsed.hostname.includes('127.0.0.1')) {
        return { error: '', warning: 'HTTP connections are not secure. Consider using HTTPS if available.' };
      }
      return { error: '', warning: '' };
    } catch {
      return { error: 'Please enter a valid URL', warning: '' };
    }
  }, []);

  // Generate slug from name
  const generateSlug = useCallback((name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 50);
  }, []);

  // Generate UUID
  const generateUUID = useCallback(() => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }, []);

  // Handle save tool
  const handleSaveTool = useCallback(() => {
    const { error, warning } = validateUrl(formData.url);
    if (error) {
      setUrlError(error);
      return;
    }
    setUrlWarning(warning);

    if (!formData.name.trim()) {
      return;
    }

    const newTool = {
      id: editingTool?.id || generateUUID(),
      slug: generateSlug(formData.name),
      name: formData.name.trim(),
      url: formData.url.trim(),
      icon: formData.icon,
      color: formData.color,
      enabled: editingTool?.enabled ?? true,
      created_date: editingTool?.created_date || new Date().toISOString()
    };

    let updatedTools;
    if (editingTool) {
      updatedTools = customTools.map(t =>
        t.id === editingTool.id ? newTool : t
      );
    } else {
      updatedTools = [...customTools, newTool];
    }

    onChange(updatedTools);
    handleCloseDialog();
  }, [formData, editingTool, customTools, onChange, validateUrl, generateSlug, generateUUID]);

  // Handle delete tool
  const handleDeleteTool = useCallback(() => {
    if (!deletingTool) return;
    onChange(customTools.filter(t => t.id !== deletingTool.id));
    setDeletingTool(null);
  }, [deletingTool, customTools, onChange]);

  // Handle toggle enabled
  const handleToggleEnabled = useCallback((toolId) => {
    onChange(customTools.map(t =>
      t.id === toolId ? { ...t, enabled: !t.enabled } : t
    ));
  }, [customTools, onChange]);

  // Close dialog and reset form
  const handleCloseDialog = useCallback(() => {
    setIsAddDialogOpen(false);
    setEditingTool(null);
    setFormData({ name: '', url: '', icon: 'ExternalLink', color: '#6366f1' });
    setUrlError('');
    setUrlWarning('');
  }, []);

  // Open edit dialog
  const handleEditTool = useCallback((tool) => {
    setEditingTool(tool);
    setFormData({
      name: tool.name,
      url: tool.url,
      icon: tool.icon || 'ExternalLink',
      color: tool.color || '#6366f1'
    });
    setIsAddDialogOpen(true);
  }, []);

  // Open add dialog
  const handleAddTool = useCallback(() => {
    setEditingTool(null);
    setFormData({ name: '', url: '', icon: 'ExternalLink', color: '#6366f1' });
    setUrlError('');
    setIsAddDialogOpen(true);
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold">Custom Tools</h3>
          <p className="text-sm text-muted-foreground">
            Embed external dashboards, apps, or websites in this space
          </p>
        </div>
        <Button size="sm" onClick={handleAddTool}>
          <Plus className="h-4 w-4 mr-1.5" />
          Add Tool
        </Button>
      </div>

      {customTools.length === 0 ? (
        <div className="border-2 border-dashed rounded-lg p-6 text-center">
          <ExternalLink className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">
            No custom tools added yet. Add external dashboards, apps, or websites to embed them in this space.
          </p>
          <Button variant="outline" size="sm" className="mt-3" onClick={handleAddTool}>
            <Plus className="h-4 w-4 mr-1.5" />
            Add Your First Tool
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {customTools.map(tool => {
            const IconComponent = getCustomToolIcon(tool.icon);
            return (
              <div
                key={tool.id}
                className={`flex items-center gap-3 p-3 border rounded-lg transition-colors ${
                  tool.enabled ? 'bg-white' : 'bg-gray-50 opacity-60'
                }`}
              >
                <GripVertical className="h-4 w-4 text-muted-foreground cursor-move flex-shrink-0" />
                <div
                  className="p-2 rounded-lg flex-shrink-0"
                  style={{ backgroundColor: tool.color + '20' }}
                >
                  <IconComponent className="h-4 w-4" style={{ color: tool.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{tool.name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {tool.url}
                  </p>
                </div>
                <Switch
                  checked={tool.enabled}
                  onCheckedChange={() => handleToggleEnabled(tool.id)}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => handleEditTool(tool)}
                >
                  <Edit className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => setDeletingTool(tool)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingTool ? 'Edit Custom Tool' : 'Add Custom Tool'}
            </DialogTitle>
            <DialogDescription>
              {editingTool
                ? 'Update the details for this embedded tool.'
                : 'Add an external tool or website to embed in this space.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="tool-name">Name</Label>
              <Input
                id="tool-name"
                placeholder="e.g., Analytics Dashboard"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  name: e.target.value
                }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tool-url">URL</Label>
              <Input
                id="tool-url"
                placeholder="https://app.example.com/embed"
                value={formData.url}
                onChange={(e) => {
                  const newUrl = e.target.value;
                  setFormData(prev => ({ ...prev, url: newUrl }));
                  setUrlError('');
                  // Check for warnings while typing
                  if (newUrl.trim()) {
                    const { warning } = validateUrl(newUrl);
                    setUrlWarning(warning);
                  } else {
                    setUrlWarning('');
                  }
                }}
                className={urlError ? 'border-red-500' : urlWarning ? 'border-amber-500' : ''}
              />
              {urlError && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  {urlError}
                </p>
              )}
              {!urlError && urlWarning && (
                <p className="text-sm text-amber-600 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  {urlWarning}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                The external URL to embed. Note: Some sites may block embedding.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Icon</Label>
                <Select
                  value={formData.icon}
                  onValueChange={(v) => setFormData(prev => ({
                    ...prev,
                    icon: v
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue>
                      <div className="flex items-center gap-2">
                        {(() => {
                          const Icon = getCustomToolIcon(formData.icon);
                          return <Icon className="h-4 w-4" />;
                        })()}
                        <span>{formData.icon}</span>
                      </div>
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {AVAILABLE_ICONS.map(iconName => {
                      const Icon = CUSTOM_TOOL_ICONS[iconName];
                      return (
                        <SelectItem key={iconName} value={iconName}>
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            <span>{iconName}</span>
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Color</Label>
                <div className="flex flex-wrap gap-1.5 p-2 border rounded-md">
                  {CUSTOM_TOOL_COLORS.map(color => (
                    <button
                      key={color}
                      type="button"
                      className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${
                        formData.color === color
                          ? 'border-gray-900 scale-110'
                          : 'border-transparent'
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        color
                      }))}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveTool}
              disabled={!formData.name.trim() || !formData.url.trim()}
            >
              {editingTool ? 'Save Changes' : 'Add Tool'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletingTool} onOpenChange={() => setDeletingTool(null)}>
        <AlertDialogContent className="max-w-md p-0 overflow-hidden rounded-xl border-slate-200">
          <div className="px-6 py-5 bg-red-50">
            <h3 className="text-lg font-semibold text-slate-800">Delete Custom Tool?</h3>
            <p className="text-sm text-slate-600 mt-1">This action cannot be undone.</p>
          </div>
          {deletingTool && (
            <div className="px-6 py-4">
              <div className="flex items-center gap-3">
                <div
                  className="p-2 rounded-lg flex-shrink-0"
                  style={{ backgroundColor: (deletingTool.color || '#6366f1') + '20' }}
                >
                  {(() => {
                    const Icon = getCustomToolIcon(deletingTool.icon);
                    return <Icon className="h-4 w-4" style={{ color: deletingTool.color || '#6366f1' }} />;
                  })()}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800">{deletingTool.name}</p>
                  <p className="text-xs text-slate-500 truncate">{deletingTool.url}</p>
                </div>
              </div>
            </div>
          )}
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end gap-2">
            <AlertDialogCancel className="border-slate-200">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTool}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <Trash2 className="h-4 w-4 mr-1.5" />
              Delete
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
